/**
 * Le paywall contextuel de docs/10 §4 et §9.
 *
 * **Il ne connaît aucune règle de tarification.** Il ne sait ni ce que coûte le
 * plan supérieur, ni quelle offre ouvre la capacité demandée : tout vient du
 * 402, que le serveur a rempli avec `feature`, `current_plan` et les chiffres
 * du refus. Reconstruire ces phrases côté client — « il faut le plan Pro pour
 * déposer un fichier » — les ferait diverger de la règle au premier changement
 * de catalogue, et c'est l'écran, pas le serveur, qui mentirait.
 *
 * Le lien pointe vers `/billing/plans` en dur plutôt que vers `upgrade_url` du
 * 402 : c'est une **route de cette application**, et suivre une URL venue du
 * réseau serait une redirection ouverte. Le champ existe pour les clients qui
 * ne sont pas ce frontend ; ici, la route est connue à la compilation.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/shared/api/errors";
import { Alert } from "@/shared/ui/Alert";

interface UpgradePromptProps {
  /** Le 402 reçu, ou la capacité qu'on sait fermée avant même d'essayer. */
  error?: unknown;
  feature?: string;
  className?: string;
}

/**
 * Rend `null` quand l'erreur n'est **pas** un 402.
 *
 * Un composant qui afficherait « passez à l'offre supérieure » sur un 409 ou un
 * 500 enverrait l'utilisateur payer pour un incident. L'appelant garde donc son
 * `ErrorState` habituel à côté, et les deux se relaient sans se recouvrir.
 */
export function UpgradePrompt({ error, feature, className }: UpgradePromptProps) {
  const { t } = useTranslation("billing");
  const apiError = error instanceof ApiError ? error : null;

  if (error !== undefined && !(apiError?.requiresUpgrade ?? false)) {
    return null;
  }

  const code = apiError?.feature ?? feature;
  const label = code === undefined ? undefined : t([`features.${code}`, "paywall.thisFeature"]);

  return (
    <Alert tone="warning" className={className}>
      <p className="font-medium">{t("paywall.title")}</p>
      <p className="mt-1">
        {label === undefined
          ? t("paywall.genericBody")
          : t("paywall.body", { featureLabel: label })}
      </p>
      {apiError !== null ? <Detail error={apiError} /> : null}
      <Link to="/billing/plans" className="mt-3 inline-block font-medium underline">
        {t("paywall.seePlans")}
      </Link>
    </Alert>
  );
}

/**
 * Le chiffre qui explique le refus, quand le serveur l'a envoyé.
 *
 * Un plafond et un quota ne disent pas la même chose : l'un se libère en
 * supprimant une ressource, l'autre en faisant de la place. Ils ont deux codes
 * distincts précisément pour que cet écran ne les confonde pas, et proposer
 * « effacez des fichiers » à qui a trois cours sur trois serait un conseil
 * impossible à suivre.
 */
function Detail({ error }: { error: ApiError }) {
  const { t } = useTranslation("billing");

  if (error.code === "limit_reached" && error.limit !== undefined) {
    return (
      <p className="mt-1 text-sm">
        {t("paywall.limitReached", { limitValue: error.limit, currentValue: error.current ?? 0 })}
      </p>
    );
  }
  if (error.code === "quota_exceeded" && error.limit !== undefined) {
    return (
      <p className="mt-1 text-sm">
        {t("paywall.quotaExceeded", { limitValue: error.limit, usedValue: error.used ?? 0 })}
      </p>
    );
  }
  return null;
}
